import { forwardRef } from 'react';
import { useSkeletonMotion } from '../skeleton/motion';

import { useMonkeysBaseAppearance, useMonkeysComponentAttributes } from '../../provider/context';
import type { BaseSkeletonProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn } from './utils';

const variantClassNames = {
  line: 'h-4 w-full',
  block: 'h-24 w-full',
  circle: 'aspect-square h-10 rounded-full',
} as const;

export const BaseSkeleton = forwardRef<HTMLDivElement, BaseSkeletonProps>(function BaseSkeleton(
  {
    variant = 'line',
    animated = true,
    animation,
    appearance,
    className,
    classNames,
    style,
    ...skeletonProps
  },
  ref,
) {
  if (variant !== 'line' && variant !== 'block' && variant !== 'circle') {
    throw new TypeError('BaseSkeleton variant must be "line", "block", or "circle".');
  }
  if (typeof animated !== 'boolean') {
    throw new TypeError('BaseSkeleton animated must be a boolean.');
  }

  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const motion = useSkeletonMotion(animation, animated, ref);

  return (
    <div
      {...componentAttributes}
      {...skeletonProps}
      ref={motion.ref}
      aria-hidden="true"
      data-monkeys-component="skeleton"
      data-slot="root"
      data-variant={variant}
      data-animated={motion.mode !== 'none'}
      data-state={motion.mode !== 'none' ? 'animated' : 'static'}
      data-skeleton-animation={motion.mode}
      className={cn(
        theme.slots.skeletonRoot,
        variantClassNames[variant],
        motion.mode === 'pulse' && 'motion-safe:animate-pulse motion-reduce:animate-none',
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.skeleton,
        ...motion.style,
        ...style,
      }}
    />
  );
});
